import React from "react"
import { Button, SIZE } from "baseui/button"
import { useStyletron } from "styletron-react"
import { useActiveObject, useEditor } from "@layerhub-io/react"
import { FontItem } from "~/interfaces/common"
import { loadFonts } from "~/utils/fonts"
import { nanoid } from "nanoid"
import { Block } from "baseui/block"
import { Input, SIZE as INPUT_SIZE } from "baseui/input"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import Search from "~/components/Icons/Search"
import Scrollable from "~/components/Scrollable"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"
import { SAMPLE_FONTS } from "~/constants/editor"
import { groupBy } from "lodash"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"

const MIN_TEXT_SIZE = 72
const MAX_TEXT_SIZE = 220

const isTextObject = (object: any) => object?.type === "StaticText" || object?.type === "DynamicText"

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export default function () {
  const editor = useEditor()
  const activeObject = useActiveObject() as any
  const { currentScene, currentDesign } = useDesignEditorContext()
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const [query, setQuery] = React.useState("")
  const [css] = useStyletron()

  const commonFonts = React.useMemo(() => {
    const grouped = groupBy(SAMPLE_FONTS, "family")
    return Object.keys(grouped)
      .map((key) => {
        const familyFonts = grouped[key]
        return (
          familyFonts.find((familyFont) => familyFont.postscript_name.includes("-Regular")) ||
          familyFonts[familyFonts.length - 1]
        )
      })
      .sort((a, b) => a.family.localeCompare(b.family))
  }, [])

  const filteredFonts = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    if (!normalizedQuery) {
      return commonFonts
    }

    return commonFonts.filter((font) => font.family.toLowerCase().includes(normalizedQuery))
  }, [commonFonts, query])

  const getSceneFrame = React.useCallback(() => {
    const liveScene = editor?.scene?.exportToJSON?.()
    return liveScene?.frame || currentScene?.frame || currentDesign?.frame || { width: 1200, height: 1200 }
  }, [currentDesign?.frame, currentScene?.frame, editor])

  const getDefaultTextSize = React.useCallback(() => {
    const frame = getSceneFrame()
    const minDimension = Math.min(frame.width || 0, frame.height || 0) || 1200
    return Math.round(clamp(minDimension * 0.075, MIN_TEXT_SIZE, MAX_TEXT_SIZE))
  }, [getSceneFrame])

  const getDefaultTextWidth = React.useCallback(() => {
    const frame = getSceneFrame()
    const width = frame.width || 1200
    return Math.round(clamp(width * 0.6, 320, width * 0.85))
  }, [getSceneFrame])

  const applyFont = React.useCallback(
    async (selectedFont?: any, options?: { preserveExistingText?: boolean }) => {
      if (!editor) {
        return
      }

      const defaultFont = commonFonts.find((font) => font.postscript_name === "OpenSans-Regular") || commonFonts[0]
      const activeFont = selectedFont || defaultFont

      if (!activeFont) {
        return
      }

      const font: FontItem = {
        name: activeFont.postscript_name,
        url: activeFont.url,
      }

      await loadFonts([font])

      if (options?.preserveExistingText && isTextObject(activeObject)) {
        editor.objects.update({
          fontFamily: font.name,
          fontURL: font.url,
        })
        return
      }

      editor.objects.add({
        id: nanoid(),
        type: "StaticText",
        width: getDefaultTextWidth(),
        text: "Add some text",
        fontSize: getDefaultTextSize(),
        fontFamily: font.name,
        textAlign: "center",
        fontURL: font.url,
        fill: "#333333",
        metadata: {},
      })
    },
    [activeObject, commonFonts, editor, getDefaultTextSize, getDefaultTextWidth]
  )

  const addComponent = async (component: any) => {
    if (editor) {
      const fontItemsList: FontItem[] = []
      if (component.objects) {
        component.objects.forEach((object: any) => {
          if (object.type === "StaticText" || object.type === "DynamicText") {
            fontItemsList.push({
              name: object.fontFamily,
              url: object.fontURL,
            })
          }
        })
        const filteredFonts = fontItemsList.filter((f) => !!f.url)
        await loadFonts(filteredFonts)
      } else {
        if (component.type === "StaticText" || component.type === "DynamicText") {
          fontItemsList.push({
            name: component.fontFamily,
            url: component.fontURL,
          })
          await loadFonts(fontItemsList)
        }
      }
      editor.objects.add(component)
    }
  }
  return (
    <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Block
        $style={{
          display: "flex",
          alignItems: "center",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "1.5rem",
        }}
      >
        <Block>Text</Block>

        <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
          <AngleDoubleLeft size={18} />
        </Block>
      </Block>
      <Scrollable>
        <Block padding={"0 1.5rem"}>
          <Button
            onClick={() => applyFont()}
            size={SIZE.compact}
            overrides={{
              Root: {
                style: {
                  width: "100%",
                },
              },
            }}
          >
            Add text
          </Button>
        </Block>
        <Block padding={"1rem 1.5rem 0"}>
          <Input
            overrides={{
              Root: {
                style: {
                  paddingLeft: "8px",
                },
              },
            }}
            clearable
            onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
            placeholder="Search fonts"
            size={INPUT_SIZE.compact}
            startEnhancer={<Search size={16} />}
            value={query}
          />
        </Block>
        <Block padding={"1rem 1.5rem 1.5rem"} display={"grid"} gridGap={"0.5rem"}>
          {filteredFonts.map((font) => (
            <Block
              key={font.id}
              onClick={() => applyFont(font, { preserveExistingText: true })}
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0.6rem 1rem",
                minHeight: "56px",
                borderRadius: "10px",
                border: "1px solid #e6e8f0",
                cursor: "pointer",
                backgroundColor: "#ffffff",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                ":hover": {
                  backgroundColor: "#f7f8fc",
                  borderColor: "#cfd5e3",
                },
              })}
            >
              <FontPreview font={font} />
            </Block>
          ))}
          {!filteredFonts.length ? (
            <Block
              $style={{
                fontSize: "13px",
                color: "#6b7280",
                textAlign: "center",
                padding: "1rem 0",
              }}
            >
              No fonts match your search.
            </Block>
          ) : null}
        </Block>
      </Scrollable>
    </Block>
  )
}

interface TextComponent {
  id: string
  metadata: {
    preview: string
  }
}

function FontPreview({ font }: { font: any }) {
  const [isLoaded, setIsLoaded] = React.useState(false)

  React.useEffect(() => {
    let isMounted = true

    loadFonts([
      {
        name: font.postscript_name,
        url: font.url,
      },
    ])
      .then(() => {
        if (isMounted) {
          setIsLoaded(true)
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsLoaded(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [font.postscript_name, font.url])

  return (
    <Block
      $style={{
        width: "100%",
        minHeight: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "26px",
        lineHeight: 1.1,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontFamily: isLoaded ? font.postscript_name : "Georgia, serif",
      }}
    >
      {font.family}
    </Block>
  )
}

function TextComponentItem({ component, onClick }: { component: any; onClick: (option: any) => void }) {
  const [css] = useStyletron()
  return (
    <div
      onClick={() => onClick(component.layers[0])}
      className={css({
        position: "relative",
        height: "84px",
        background: "#f8f8fb",
        cursor: "pointer",
        padding: "12px",
        borderRadius: "8px",
        overflow: "hidden",
        "::before:hover": {
          opacity: 1,
        },
      })}
    >
      <div
        className={css({
          backgroundImage: `linear-gradient(to bottom,
          rgba(0, 0, 0, 0) 0,
          rgba(0, 0, 0, 0.006) 8.1%,
          rgba(0, 0, 0, 0.022) 15.5%,
          rgba(0, 0, 0, 0.047) 22.5%,
          rgba(0, 0, 0, 0.079) 29%,
          rgba(0, 0, 0, 0.117) 35.3%,
          rgba(0, 0, 0, 0.158) 41.2%,
          rgba(0, 0, 0, 0.203) 47.1%,
          rgba(0, 0, 0, 0.247) 52.9%,
          rgba(0, 0, 0, 0.292) 58.8%,
          rgba(0, 0, 0, 0.333) 64.7%,
          rgba(0, 0, 0, 0.371) 71%,
          rgba(0, 0, 0, 0.403) 77.5%,
          rgba(0, 0, 0, 0.428) 84.5%,
          rgba(0, 0, 0, 0.444) 91.9%,
          rgba(0, 0, 0, 0.45) 100%)`,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0,
          transition: "opacity 0.3s ease-in-out",
          height: "100%",
          width: "100%",
          ":hover": {
            opacity: 1,
          },
        })}
      ></div>
      <img
        src={component.preview}
        className={css({
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
          verticalAlign: "middle",
        })}
      />
    </div>
  )
}
